import React from 'react';
import { Facebook, Instagram, Linkedin, Twitter, Youtube } from 'lucide-react';

// TikTok doesn't ship with lucide-react — render as inline SVG.
const TikTokIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.28a8.16 8.16 0 0 0 4.77 1.52V7.35a4.85 4.85 0 0 1-1.84-.66Z" />
  </svg>
);

// Metadata for each platform — keeps order consistent + gives us host validation.
const SOCIAL_PLATFORMS = [
  { key: 'facebook',   label: 'Facebook',   Icon: Facebook,    host: ['facebook.com', 'fb.com', 'fb.me'], colour: 'hover:text-[#1877F2]' },
  { key: 'instagram',  label: 'Instagram',  Icon: Instagram,   host: ['instagram.com'],                    colour: 'hover:text-[#E4405F]' },
  { key: 'linkedin',   label: 'LinkedIn',   Icon: Linkedin,    host: ['linkedin.com', 'lnkd.in'],          colour: 'hover:text-[#0A66C2]' },
  { key: 'twitter_x',  label: 'X / Twitter', Icon: Twitter,    host: ['twitter.com', 'x.com'],             colour: 'hover:text-slate-900' },
  { key: 'tiktok',     label: 'TikTok',     Icon: TikTokIcon,  host: ['tiktok.com'],                       colour: 'hover:text-slate-900' },
  { key: 'youtube',    label: 'YouTube',    Icon: Youtube,     host: ['youtube.com', 'youtu.be'],          colour: 'hover:text-[#FF0000]' },
];

/**
 * Normalise a user-entered social URL.
 * Returns `null` if the URL is empty or doesn't match one of the known hosts.
 * This prevents users from sneaking phishing links into the social block.
 */
export const normaliseSocialUrl = (platformKey, value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const withProtocol = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
  try {
    const u = new URL(withProtocol);
    const platform = SOCIAL_PLATFORMS.find(p => p.key === platformKey);
    if (!platform) return null;
    const host = u.hostname.replace(/^www\./, '').toLowerCase();
    if (!platform.host.some(h => host === h || host.endsWith(`.${h}`))) return null;
    return withProtocol;
  } catch {
    return null;
  }
};

const SocialLinks = ({ socialLinks, variant = 'row', onClick }) => {
  if (!socialLinks || typeof socialLinks !== 'object') return null;

  // Normalise & filter to known/valid entries.
  const entries = SOCIAL_PLATFORMS
    .map(p => ({ ...p, url: normaliseSocialUrl(p.key, socialLinks[p.key]) }))
    .filter(e => !!e.url);

  if (entries.length === 0) return null;

  if (variant === 'row') {
    return (
      <div className="pt-4 mt-4 border-t" data-testid="listing-social-links">
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Follow & Connect</p>
        <div className="flex flex-wrap gap-2">
          {entries.map(({ key, label, Icon, colour, url }) => (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClick}
              className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 text-slate-600 transition-colors ${colour}`}
              aria-label={label}
              title={label}
              data-testid={`social-link-${key}`}
            >
              <Icon className="h-5 w-5" />
            </a>
          ))}
        </div>
      </div>
    );
  }

  // compact: inline pill layout
  return (
    <div className="flex flex-wrap gap-2" data-testid="listing-social-links">
      {entries.map(({ key, label, Icon, colour, url }) => (
        <a
          key={key}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-sm text-slate-700 transition-colors ${colour}`}
          aria-label={label}
          title={label}
          data-testid={`social-link-${key}`}
        >
          <Icon className="h-4 w-4" />
          {label}
        </a>
      ))}
    </div>
  );
};

export { SOCIAL_PLATFORMS };
export default SocialLinks;
